import React, { useState, useEffect } from 'react';
import { OpeningHours, DaySchedule, ExceptionDate, TimeSlot, DayOfWeek } from '../types';
import { apiService } from '../services/api';
import { addDaysLondon, formatLondonDateTime, LONDON_TZ, londonDateKey } from '../shared/dateUtils';
import './OpeningHoursManagement.css';

const OpeningHoursManagement: React.FC = () => {
    const [openingHours, setOpeningHours] = useState<OpeningHours | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const dayNames: Record<DayOfWeek, string> = {
        'sunday': 'Sunday',
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday'
    };

    useEffect(() => {
        fetchOpeningHours();
    }, []);

    const fetchOpeningHours = async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await apiService.getOpeningHours();
            setOpeningHours(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch opening hours');
            console.error('Opening hours fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const saveOpeningHours = async () => {
        if (!openingHours) return;

        try {
            setSaving(true);
            setError(null);
            setSuccessMessage(null);

            await apiService.updateOpeningHours(openingHours);

            setSuccessMessage('Opening hours updated successfully!');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save opening hours');
            console.error('Save opening hours error:', err);
        } finally {
            setSaving(false);
        }
    };

    const updateDaySchedule = (dayOfWeek: DayOfWeek, updates: Partial<DaySchedule>) => {
        if (!openingHours) return;

        const updatedSchedule = openingHours.weeklySchedule.map(day =>
            day.dayOfWeek === dayOfWeek ? { ...day, ...updates } : day
        );

        setOpeningHours({
            ...openingHours,
            weeklySchedule: updatedSchedule
        });
    };

    const updateTimeSlot = (dayOfWeek: DayOfWeek, slotIndex: number, updates: Partial<TimeSlot>) => {
        if (!openingHours) return;

        const updatedSchedule = openingHours.weeklySchedule.map(day => {
            if (day.dayOfWeek === dayOfWeek) {
                const updatedSlots = day.timeSlots.map((slot, index) =>
                    index === slotIndex ? { ...slot, ...updates } : slot
                );
                return { ...day, timeSlots: updatedSlots };
            }
            return day;
        });

        setOpeningHours({
            ...openingHours,
            weeklySchedule: updatedSchedule
        });
    };

    const addTimeSlot = (dayOfWeek: DayOfWeek) => {
        if (!openingHours) return;

        const updatedSchedule = openingHours.weeklySchedule.map(day => {
            if (day.dayOfWeek === dayOfWeek) {
                return {
                    ...day,
                    timeSlots: [...day.timeSlots, { start: '09:00', end: '17:00' }]
                };
            }
            return day;
        });

        setOpeningHours({
            ...openingHours,
            weeklySchedule: updatedSchedule
        });
    };

    const removeTimeSlot = (dayOfWeek: DayOfWeek, slotIndex: number) => {
        if (!openingHours) return;

        const updatedSchedule = openingHours.weeklySchedule.map(day => {
            if (day.dayOfWeek === dayOfWeek) {
                return {
                    ...day,
                    timeSlots: day.timeSlots.filter((_, index) => index !== slotIndex)
                };
            }
            return day;
        });

        setOpeningHours({
            ...openingHours,
            weeklySchedule: updatedSchedule
        });
    };

    const addException = () => {
        if (!openingHours) return;

        const dateStr = addDaysLondon(londonDateKey(), 1);

        const newException: ExceptionDate = {
            date: dateStr,
            isOpen: false,
            timeSlots: [],
            reason: 'Special Event'
        };

        setOpeningHours({
            ...openingHours,
            exceptions: [...openingHours.exceptions, newException]
        });
    };

    const updateException = (index: number, updates: Partial<ExceptionDate>) => {
        if (!openingHours) return;

        const updatedExceptions = openingHours.exceptions.map((exception, i) =>
            i === index ? { ...exception, ...updates } : exception
        );

        setOpeningHours({
            ...openingHours,
            exceptions: updatedExceptions
        });
    };

    const removeException = (index: number) => {
        if (!openingHours) return;

        setOpeningHours({
            ...openingHours,
            exceptions: openingHours.exceptions.filter((_, i) => i !== index)
        });
    };

    const addExceptionTimeSlot = (exceptionIndex: number) => {
        if (!openingHours) return;

        const updatedExceptions = openingHours.exceptions.map((exception, index) => {
            if (index === exceptionIndex) {
                return {
                    ...exception,
                    timeSlots: [...exception.timeSlots, { start: '09:00', end: '17:00' }]
                };
            }
            return exception;
        });

        setOpeningHours({
            ...openingHours,
            exceptions: updatedExceptions
        });
    };

    const removeExceptionTimeSlot = (exceptionIndex: number, slotIndex: number) => {
        if (!openingHours) return;

        const updatedExceptions = openingHours.exceptions.map((exception, index) => {
            if (index === exceptionIndex) {
                return {
                    ...exception,
                    timeSlots: exception.timeSlots.filter((_, i) => i !== slotIndex)
                };
            }
            return exception;
        });

        setOpeningHours({
            ...openingHours,
            exceptions: updatedExceptions
        });
    };

    const updateExceptionTimeSlot = (exceptionIndex: number, slotIndex: number, updates: Partial<TimeSlot>) => {
        if (!openingHours) return;

        const updatedExceptions = openingHours.exceptions.map((exception, index) => {
            if (index === exceptionIndex) {
                const updatedSlots = exception.timeSlots.map((slot, i) =>
                    i === slotIndex ? { ...slot, ...updates } : slot
                );
                return { ...exception, timeSlots: updatedSlots };
            }
            return exception;
        });

        setOpeningHours({
            ...openingHours,
            exceptions: updatedExceptions
        });
    };

    if (loading) {
        return (
            <div className="opening-hours-loading">
                <div className="loading-spinner"></div>
                <p>Loading opening hours...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="opening-hours-error">
                <div className="error-icon">⚠️</div>
                <h3>Error Loading Opening Hours</h3>
                <p>{error}</p>
                <button className="retry-button" onClick={fetchOpeningHours}>
                    Retry
                </button>
            </div>
        );
    }

    if (!openingHours) {
        return null;
    }

    return (
        <div className="opening-hours-management">
            <div className="opening-hours-header">
                <h2>🕒 Opening Hours Management</h2>
                <div className="header-actions">
                    <button
                        className="save-button"
                        onClick={saveOpeningHours}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : '💾 Save Changes'}
                    </button>
                </div>
            </div>

            {successMessage && (
                <div className="success-message">
                    <span className="success-icon">✅</span>
                    {successMessage}
                </div>
            )}

            {error && (
                <div className="error-message">
                    <span className="error-icon">❌</span>
                    {error}
                </div>
            )}

            {/* Delay Alert Toggle */}
            <div className="delay-alert-section">
                <div className="delay-alert-card">
                    <div className="delay-alert-header">
                        <div className="delay-alert-title">
                            <h3>⏰ Delivery Delay Alert</h3>
                            <p className="delay-alert-subtitle">
                                Show a notice to customers when experiencing high order volume
                            </p>
                        </div>
                        <label className="delay-toggle">
                            <input
                                type="checkbox"
                                checked={openingHours.isDelayActive || false}
                                onChange={(e) => setOpeningHours({
                                    ...openingHours,
                                    isDelayActive: e.target.checked
                                })}
                            />
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">
                                {openingHours.isDelayActive ? 'Active' : 'Inactive'}
                            </span>
                        </label>
                    </div>
                    {openingHours.isDelayActive && (
                        <div className="delay-alert-preview">
                            <div className="preview-label">Customer will see:</div>
                            <div className="preview-modal">
                                <h4>We're experiencing high demand right now — thank you for keeping us busy!</h4>
                                <p>We're still accepting orders as normal, though deliveries might take a little longer.</p>
                                <p>We truly appreciate your patience — your meal is being cooked fresh, with care and love. ❤️</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Weekly Schedule */}
            <div className="schedule-section">
                <h3>📅 Weekly Schedule</h3>
                <div className="days-grid">
                    {openingHours.weeklySchedule.map((day) => (
                        <div key={day.dayOfWeek} className="day-card">
                            <div className="day-header">
                                <h4>{dayNames[day.dayOfWeek]}</h4>
                                <label className="day-toggle">
                                    <input
                                        type="checkbox"
                                        checked={day.isOpen}
                                        onChange={(e) => updateDaySchedule(day.dayOfWeek, {
                                            isOpen: e.target.checked,
                                            timeSlots: e.target.checked ? day.timeSlots : []
                                        })}
                                    />
                                    <span className="toggle-text">
                                        {day.isOpen ? 'Open' : 'Closed'}
                                    </span>
                                </label>
                            </div>

                            {day.isOpen && (
                                <div className="time-slots">
                                    {day.timeSlots.map((slot, slotIndex) => (
                                        <div key={slotIndex} className="time-slot">
                                            <input
                                                type="time"
                                                value={slot.start}
                                                onChange={(e) => updateTimeSlot(day.dayOfWeek, slotIndex, { start: e.target.value })}
                                                className="time-input"
                                            />
                                            <span className="time-separator">to</span>
                                            <input
                                                type="time"
                                                value={slot.end}
                                                onChange={(e) => updateTimeSlot(day.dayOfWeek, slotIndex, { end: e.target.value })}
                                                className="time-input"
                                            />
                                            {day.timeSlots.length > 1 && (
                                                <button
                                                    className="remove-slot-button"
                                                    onClick={() => removeTimeSlot(day.dayOfWeek, slotIndex)}
                                                    title="Remove time slot"
                                                >
                                                    ❌
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        className="add-slot-button"
                                        onClick={() => addTimeSlot(day.dayOfWeek)}
                                    >
                                        + Add Time Slot
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Exception Dates */}
            <div className="schedule-section">
                <div className="section-header">
                    <h3>📆 Exception Dates</h3>
                    <button className="add-exception-button" onClick={addException}>
                        + Add Exception
                    </button>
                </div>

                {openingHours.exceptions.length === 0 ? (
                    <div className="no-exceptions">
                        <p>No exception dates configured. Add special dates like holidays or events.</p>
                    </div>
                ) : (
                    <div className="exceptions-list">
                        {openingHours.exceptions.map((exception, index) => (
                            <div key={index} className="exception-card">
                                <div className="exception-header">
                                    <input
                                        type="date"
                                        value={exception.date}
                                        onChange={(e) => updateException(index, { date: e.target.value })}
                                        className="date-input"
                                    />
                                    <input
                                        type="text"
                                        value={exception.reason || ''}
                                        onChange={(e) => updateException(index, { reason: e.target.value })}
                                        placeholder="Reason (optional)"
                                        className="reason-input"
                                    />
                                    <label className="exception-toggle">
                                        <input
                                            type="checkbox"
                                            checked={exception.isOpen}
                                            onChange={(e) => updateException(index, {
                                                isOpen: e.target.checked,
                                                timeSlots: e.target.checked ? exception.timeSlots : []
                                            })}
                                        />
                                        <span className="toggle-text">
                                            {exception.isOpen ? 'Open' : 'Closed'}
                                        </span>
                                    </label>
                                    <button
                                        className="remove-exception-button"
                                        onClick={() => removeException(index)}
                                        title="Remove exception"
                                    >
                                        🗑️
                                    </button>
                                </div>

                                {exception.isOpen && (
                                    <div className="exception-time-slots">
                                        {exception.timeSlots.map((slot, slotIndex) => (
                                            <div key={slotIndex} className="time-slot">
                                                <input
                                                    type="time"
                                                    value={slot.start}
                                                    onChange={(e) => updateExceptionTimeSlot(index, slotIndex, { start: e.target.value })}
                                                    className="time-input"
                                                />
                                                <span className="time-separator">to</span>
                                                <input
                                                    type="time"
                                                    value={slot.end}
                                                    onChange={(e) => updateExceptionTimeSlot(index, slotIndex, { end: e.target.value })}
                                                    className="time-input"
                                                />
                                                <button
                                                    className="remove-slot-button"
                                                    onClick={() => removeExceptionTimeSlot(index, slotIndex)}
                                                    title="Remove time slot"
                                                >
                                                    ❌
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            className="add-slot-button"
                                            onClick={() => addExceptionTimeSlot(index)}
                                        >
                                            + Add Time Slot
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Timezone Info */}
            <div className="timezone-info">
                <p>
                    <strong>Timezone:</strong> {LONDON_TZ} (UK Time)
                </p>
                <p>
                    <strong>Last Updated:</strong> {formatLondonDateTime(openingHours.lastUpdated)}
                </p>
            </div>
        </div>
    );
};

export default OpeningHoursManagement;
